import { API_ENDPOINT } from "@/constants/appConstants";
import { useProfileSectionData } from "@/hooks";
import { profileAPI } from "@/lib/api/api";
import {
  AI_SUMMARY,
  PROFILE_QUERY_KEY,
  PROFILE_STATS_QUERY_KEY,
  PROFILE_SUMMARY_QUERY_KEY,
} from "@/lib/queryKeys";
import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { ProfileSummaryApiResponse } from "@/components/profile/profileSummary/types";
import { ErrorResponse } from "@/components/profile/types";

// export const getProfileSummary = (): UseQueryResult<any, Error> => {
//   return useQuery({
//     queryKey: [PROFILE_SUMMARY_QUERY_KEY],
//     queryFn: () => {
//       return profileAPI<ProfileSummaryApiResponse>("additional_profiles");
//     },
//   });
// };

export const getProfileSummary = () => {
  return useProfileSectionData<ProfileSummaryApiResponse>(
    "manageProfileSection",
  );
};

// to get initial ai summary data
export const getGeneratedAISummary = (): UseQueryResult<any, Error> => {
  return useQuery({
    queryKey: [AI_SUMMARY],
    queryFn: async () => {
      const response = await fetch(
        API_ENDPOINT(window.location.host).middleware.getAiSummary,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );
      if (!response.ok) {
        const errorData = (await response.json()) as ErrorResponse;
        throw new Error(
          errorData.error.message.errorMessage || "Something went wrong",
        );
      }

      return await response.json();
    },
  });
};

// to get id of generated summary
export const useGetAiSummaryId = () => {
  return useMutation({
    mutationFn: async ({ data }: { data: any }) => {
      const response = await fetch(
        API_ENDPOINT(window.location.host).middleware.getAiSummaryRequestId,
        {
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        const errorData = (await response.json()) as ErrorResponse;
        throw new Error(
          errorData.error.message.errorMessage || "Something went wrong",
        );
      }

      return await response.json();
    },
  });
};

// to get generated summary using id
export const useGetAiSummary = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ params }: { params?: Record<string, any> }) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(
        `${API_ENDPOINT(window.location.host).middleware.getAiSummary}${
          queryString ? `?${queryString}` : ""
        }`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );
      if (!response.ok) {
        const errorData = (await response.json()) as ErrorResponse;
        throw new Error(
          errorData.error.message.errorMessage || "Something went wrong",
        );
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AI_SUMMARY] });
    },
  });
};

export const useUpdateProfileSummary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      data,
      params,
    }: {
      data: any;
      params?: Record<string, any>;
    }) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(
        `${API_ENDPOINT(window.location.host).middleware.setSummary}${queryString ? `?${queryString}` : ""}`,
        {
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        const errorData = (await response.json()) as ErrorResponse;
        throw new Error(
          errorData.error.message.errorMessage || "Something went wrong",
        );
      }

      return await response.json();
    },
    onSuccess: () => {
      // queryClient.invalidateQueries({ queryKey: [PROFILE_SUMMARY_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PROFILE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [AI_SUMMARY] });
      queryClient.invalidateQueries({ queryKey: [PROFILE_STATS_QUERY_KEY] });
    },
  });
};
